import { Injectable, OnDestroy } from '@angular/core';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { Observable, Subject } from 'rxjs';
import { environment } from '@environment';

@Injectable({ providedIn: 'root' })
export class WebsocketService implements OnDestroy {
  private socket$: WebSocketSubject<any> | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private userId = '';
  private destroyed = false;
  private connected = false;
  private readonly incomingMessages = new Subject<any>();
  private readonly opened = new Subject<void>();

  connect(userId: string) {
    if (this.destroyed || (this.userId === userId && this.socket$)) return;

    this.stopConnection();
    this.userId = userId;
    this.reconnectAttempts = 0;
    if (userId) this.initializeWebSocket();
  }

  private initializeWebSocket() {
    if (this.destroyed || !this.userId) return;

    const socket = webSocket<any>({
      url: `${environment.signalingServerUrl}/${this.userId}`,
      openObserver: {
        next: () => {
          if (this.socket$ !== socket) return;
          this.connected = true;
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          this.opened.next();
        }
      }
    });

    this.socket$ = socket;
    socket.subscribe({
      next: message => {
        if (this.socket$ === socket) this.incomingMessages.next(message);
      },
      error: () => this.handleDisconnect(socket),
      complete: () => this.handleDisconnect(socket)
    });
  }

  sendMessage(message: any) {
    this.socket$?.next(message);
  }

  getMessages(): Observable<any> {
    return this.incomingMessages.asObservable();
  }

  getConnectionOpened(): Observable<void> {
    return this.opened.asObservable();
  }

  get isConnected(): boolean {
    return this.connected;
  }

  private handleDisconnect(socket: WebSocketSubject<any>) {
    if (this.socket$ !== socket) return;

    this.socket$ = null;
    this.connected = false;
    this.stopHeartbeat();
    if (this.destroyed || this.reconnectTimer || this.reconnectAttempts >= this.maxReconnectAttempts) return;

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.initializeWebSocket();
    }, delay);
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.sendMessage({ type: 'ping' }), 60000);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private stopConnection() {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopHeartbeat();
    const socket = this.socket$;
    this.socket$ = null;
    this.connected = false;
    socket?.complete();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.stopConnection();
    this.incomingMessages.complete();
    this.opened.complete();
  }
}
